import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  api, ApiError, hasSessionHint, markSession, setAccessToken, setSessionLostHandler,
} from '@/lib/api';
import type { Role, User } from '@/lib/types';

interface AuthState {
  user: User | null;
  /** true enquanto a sessão é restaurada no primeiro carregamento. */
  initializing: boolean;
  /** `identifier` é o nome de usuário ou o e-mail — o servidor aceita os dois. */
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Aplica o perfil que uma alteração já devolveu, sem outra ida ao servidor. */
  applyUser: (profile: User) => void;
  /** Papel mínimo na hierarquia ADMIN > MEMBER. */
  hasRole: (role: Role) => boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const RANK: Record<Role, number> = { ADMIN: 2, MEMBER: 1 };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Sem pista de sessão não há o que restaurar: já começa pronto, sem
  // disparar um refresh fadado ao 401.
  const [initializing, setInitializing] = useState(() => hasSessionHint());
  const queryClient = useQueryClient();

  // Restaura a sessão pelo cookie de refresh ao abrir o app.
  useEffect(() => {
    if (!hasSessionHint()) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const renewed = await api.refreshSession();
        if (renewed && !cancelled) {
          const { user: profile } = await api.get<{ user: User }>('/api/auth/me');
          if (!cancelled) setUser(profile);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Um 401 irrecuperável derruba o usuário para a tela de login.
  useEffect(() => {
    setSessionLostHandler(() => {
      markSession(false);
      setUser(null);
      queryClient.clear();
    });
    return () => setSessionLostHandler(null);
  }, [queryClient]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const data = await api.post<{ user: User; accessToken: string }>('/api/auth/login', {
        login: identifier,
        password,
      });
      setAccessToken(data.accessToken);
      markSession(true);
      setUser(data.user);
      queryClient.clear();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Sessão já inválida no servidor: seguir com o logout local mesmo assim.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      setAccessToken(null);
      markSession(false);
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    const { user: profile } = await api.get<{ user: User }>('/api/auth/me');
    setUser(profile);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      initializing,
      login,
      logout,
      refreshUser,
      applyUser: setUser,
      hasRole: (role: Role) => (user ? RANK[user.role] >= RANK[role] : false),
      isAdmin: user?.role === 'ADMIN',
    }),
    [user, initializing, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}
